import { useEffect, useState } from 'react';
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { AlertTriangle, Copy, FileText, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useDeleteDocument, useCloneDocument, useUpdateDocument } from '@/hooks/documents/useDocumentMutations';
import { useDocumentContent } from '@/hooks/documents/useDocumentContent';
import { useProfiles } from '@/hooks/auth/useProfiles';
import { useAuth } from '@/auth/AuthProvider';
import { DocumentWithMetadata, DocumentTypeIconMap } from '@/types/knowledge/knowledge-base';
import { formatFileSize } from '@/utils/formatting/general';
import { DocumentTypeIcon } from '@/components/knowledge-base/DocumentTypeIcon';

const getSourceTypeLabel = (sourceType: string | null | undefined): string => {
  if (!sourceType) return "";
  return (
    DocumentTypeIconMap[sourceType as keyof typeof DocumentTypeIconMap]?.label ||
    sourceType
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  );
};

interface KnowledgeBaseDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  document: DocumentWithMetadata | null;
  documentId?: string;
  onError?: (error: string) => void;
}

export function KnowledgeBaseDetailsDialog({
  isOpen,
  onClose,
  document,
  onError,
}: KnowledgeBaseDetailsDialogProps) {
  const [currentDocument, setCurrentDocument] = useState<DocumentWithMetadata | null>(document);
  const [isCloneDialogOpen, setIsCloneDialogOpen] = useState(false);
  const [cloneName, setCloneName] = useState('');
  const [targetUserId, setTargetUserId] = useState('');
  const [isReassignDialogOpen, setIsReassignDialogOpen] = useState(false);
  const [reassignUserId, setReassignUserId] = useState('');
  const deleteDocument = useDeleteDocument();
  const cloneDocument = useCloneDocument();
  const updateDocument = useUpdateDocument();
  const { userProfile } = useAuth();
  const { data: profiles = [], isLoading: isLoadingProfiles } = useProfiles();
  const { urlContent, isLoading } = useDocumentContent(currentDocument, isOpen);

  useEffect(() => {
    setCurrentDocument(document);
    setCloneName('');
    setTargetUserId('');
    setReassignUserId('');
    setIsReassignDialogOpen(false);
  }, [document]);

  const handleDelete = () => {
    if (!currentDocument) return;

    const hasDependent =
      currentDocument.dependent_agents && currentDocument.dependent_agents.length > 0;
    const confirmMessage = hasDependent
      ? `This will remove "${currentDocument.name}" from ${currentDocument.dependent_agents.length} dependent agent(s) and from the Knowledge Base. The original document will remain in your library. Are you sure?`
      : `Are you sure you want to remove "${currentDocument.name}" from the Knowledge Base? The original document will remain in your library.`;

    if (!confirm(confirmMessage)) return;

    deleteDocument.mutate(currentDocument.id, {
      onSuccess: () => {
        onClose();
      },
      onError: (error) => {
        onError?.(error.message || "Failed to delete document");
      },
    });
  };

  const handleClone = () => {
    if (!currentDocument) return;

    const data: { documentId: string; name?: string; userId?: string } = {
      documentId: currentDocument.id,
    };

    if (cloneName.trim()) {
      data.name = cloneName.trim();
    }

    if (targetUserId.trim()) {
      data.userId = targetUserId.trim();
    }

    cloneDocument.mutate(data, {
      onSuccess: () => {
        setIsCloneDialogOpen(false);
        setCloneName('');
        setTargetUserId('');
      },
      onError: (error) => {
        onError?.(error.message || "Failed to clone document");
      },
    });
  };

  const handleReassign = () => {
    if (!currentDocument || !reassignUserId.trim()) return;

    updateDocument.mutate(
      {
        document: currentDocument,
        updates: {
          ...currentDocument,
          user_id: reassignUserId.trim(),
        },
      },
      {
        onSuccess: (updatedDoc) => {
          setCurrentDocument(updatedDoc);
          setIsReassignDialogOpen(false);
          setReassignUserId('');
        },
        onError: (error) => {
          onError?.(error.message || "Failed to reassign document");
        },
      }
    );
  };

  if (!isOpen || !currentDocument) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[80]" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-[90vw] sm:w-[70vw] lg:w-[70vw] xl:w-[60vw] max-w-[1200px] bg-white shadow-xl z-[80] flex flex-col overflow-hidden transform transition-transform duration-300 ease-in-out">
        <div className="flex items-center justify-between p-6 border-b bg-white">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0">
              <DocumentTypeIcon document={currentDocument} showTooltip={true} iconSize="md" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {currentDocument.name || "Untitled Document"}
              </h2>
              <p className="text-sm text-gray-500">Knowledge Base Document</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="bg-gray-50 border-b px-6 py-4">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 text-sm">
            {currentDocument.source_type && (
              <div>
                <span className="font-medium text-gray-600">Type:</span>
                <p className="text-gray-900">{getSourceTypeLabel(currentDocument.source_type)}</p>
              </div>
            )}
            {currentDocument.metadata?.pdf_size && (
              <div>
                <span className="font-medium text-gray-600">Size:</span>
                <p className="text-gray-900">{formatFileSize(currentDocument.metadata.pdf_size)}</p>
              </div>
            )}
            {currentDocument.created_at && (
              <div>
                <span className="font-medium text-gray-600">Created:</span>
                <p className="text-gray-900">
                  {new Date(currentDocument.created_at).toLocaleDateString()}
                </p>
              </div>
            )}
            {currentDocument.owner && (
              <div>
                <span className="font-medium text-gray-600">Author:</span>
                <p className="text-gray-900">
                  <CopyableEmail email={currentDocument.owner.full_name || currentDocument.owner.email} />
                </p>
              </div>
            )}
          </div>
        </div>

        {currentDocument.dependent_agents?.length > 0 && (
          <div className="px-6 py-4 border-b bg-blue-50">
            <h3 className="font-semibold text-gray-900 mb-2">Associated Agents</h3>
            <div className="flex flex-wrap gap-2">
              {currentDocument.dependent_agents.map((agent) => (
                <span
                  key={agent.id}
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                >
                  {agent.name}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Document Content</h3>
                <span className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded">
                  ID: {currentDocument.id}
                </span>
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-500">Loading document content...</p>
                  </div>
                </div>
              ) : urlContent ? (
                <div className="bg-gray-50 rounded-lg p-4 border">
                  <pre className="whitespace-pre-wrap text-sm font-mono text-gray-800 leading-relaxed max-h-[500px] overflow-y-auto">
                    {urlContent}
                  </pre>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No content available for this document</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border-t bg-white p-6">
          <div className="flex justify-between">
            <div className="flex items-center gap-3">
              {currentDocument.dependent_agents && currentDocument.dependent_agents.length > 0 ? (
                <div className="flex items-center gap-2 text-amber-600 text-sm">
                  <AlertTriangle className="h-4 w-4" />
                  <span>
                    Will update {currentDocument.dependent_agents.length} dependent agent(s)
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-green-600 text-sm">
                  <span>No dependent agents - safe to remove</span>
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => setIsCloneDialogOpen(true)}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Copy className="h-4 w-4" />
                Clone
              </Button>
              <Button
                onClick={() => setIsReassignDialogOpen(true)}
                variant="outline"
                className="flex items-center gap-2"
              >
                Reassign
              </Button>
              <Button
                onClick={handleDelete}
                disabled={deleteDocument.isPending}
                variant="destructive"
                className="flex items-center gap-2"
              >
                {deleteDocument.isPending ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Removing...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </>
                )}
              </Button>
              <Button onClick={onClose} variant="outline">
                Close
              </Button>
            </div>
          </div>
        </div>

        {isCloneDialogOpen && (
          <>
            <div className="fixed inset-0 bg-black/50 z-[90]" onClick={() => setIsCloneDialogOpen(false)} />
            <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl z-[90] w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Clone Document</h3>
                <button
                  onClick={() => setIsCloneDialogOpen(false)}
                  className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Document Name
                  </label>
                  <Input
                    value={cloneName}
                    onChange={(e) => setCloneName(e.target.value)}
                    placeholder={`Copy of ${currentDocument.name}`}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Clone for User (Optional)
                  </label>
                  <select
                    value={targetUserId}
                    onChange={(e) => setTargetUserId(e.target.value)}
                    disabled={isLoadingProfiles}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">Current User</option>
                    {[...profiles]
                      .sort((a, b) => {
                        const nameA = (a.full_name || a.email).toLowerCase();
                        const nameB = (b.full_name || b.email).toLowerCase();
                        return nameA.localeCompare(nameB);
                      })
                      .map((profile) => (
                        <option key={profile.id} value={profile.id}>
                          {profile.full_name || profile.email}
                        </option>
                      ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Select a user to clone for that user</p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={handleClone}
                    disabled={cloneDocument.isPending}
                    variant="default"
                    className="flex-1 flex items-center justify-center gap-2"
                  >
                    {cloneDocument.isPending ? (
                      <>
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Cloning...
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4" />
                        Clone
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => setIsCloneDialogOpen(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}

        {isReassignDialogOpen && (
          <>
            <div className="fixed inset-0 bg-black/50 z-[90]" onClick={() => setIsReassignDialogOpen(false)} />
            <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl z-[90] w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Reassign Document</h3>
                <button
                  onClick={() => setIsReassignDialogOpen(false)}
                  className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assign to User
                  </label>
                  <select
                    value={reassignUserId}
                    onChange={(e) => setReassignUserId(e.target.value)}
                    disabled={isLoadingProfiles}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">Select a user...</option>
                    {[...profiles]
                      .sort((a, b) => {
                        const nameA = (a.full_name || a.email).toLowerCase();
                        const nameB = (b.full_name || b.email).toLowerCase();
                        return nameA.localeCompare(nameB);
                      })
                      .map((profile) => (
                        <option key={profile.id} value={profile.id}>
                          {profile.full_name || profile.email}
                        </option>
                      ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Choose which user should own this document</p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={handleReassign}
                    disabled={updateDocument.isPending || !reassignUserId.trim()}
                    variant="default"
                    className="flex-1"
                  >
                    {updateDocument.isPending ? (
                      <>
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                        Reassigning...
                      </>
                    ) : (
                      'Reassign'
                    )}
                  </Button>
                  <Button
                    onClick={() => setIsReassignDialogOpen(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
